import { Alert } from 'avo-ui'
import { NominationVoting } from 'components/award/voting/create-voting/NominationVoting'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { API_AWARD } from 'services/apiAward'
import { useFetch } from 'services/useFetch'
import { uploadFileToS3 } from 'utils/utils'

const INITIAL_STATE = {
    title_image_url: null,
    mini_banner_url: null,
    nominations: [],
}

const NOMINEE_TYPE = {
    PRODUCT: 'product',
    BRAND: 'brand',
}

export const NominationVotingContainer = ({ awardId, data, onSuccessUpdate, id, type }) => {
    const [dataGeneral, setDataGeneral] = useState({})
    const [nominationDetail, setNominationDetail] = useState(INITIAL_STATE)
    const [errorForm, setErrorForm] = useState({})
    const copyItems = [...(nominationDetail?.nominations || [])]
    const [optionsNominee, setOptionsNominee] = useState([])
    const { fetching: getProductList } = useFetch(API_AWARD.PRODUCT_LIST)
    const { fetching: getBrandList } = useFetch(API_AWARD.BRAND_LIST)
    const { fetching: updateAward, isLoading } = useFetch({
        ...API_AWARD.AWARD_UPDATE,
        initPathVariable: awardId,
    })
    const { fetching: getURLImage, isLoading: isLoadingUpdate } = useFetch(API_AWARD.URL_AWARD_IMAGE)

    useEffect(() => {
        if (data) {
            const { start_date, end_date, ...rest } = data
            setDataGeneral({
                ...rest,
                start_date: moment(start_date).format('YYYY-MM-DD HH:mm'),
                end_date: moment(end_date).format('YYYY-MM-DD HH:mm'),
            })

            if (data?.categories?.[id]) {
                constructNominee()
            }
        }
    }, [data, id])

    const constructNominee = async () => {
        let nomineeList = []
        const response = type === NOMINEE_TYPE.PRODUCT ? await getProductList() : await getBrandList()
        nomineeList = response?.data || []

        const nominationData = data?.categories?.[id]

        setNominationDetail({
            ...nominationData,
            nominations:
                nominationData?.nominations?.map((item) => ({
                    ...item,
                    nominee_ids:
                        item?.nominee_ids?.map((nominee) => {
                            const found = nomineeList.find((n) => n.id === nominee) || {}
                            return {
                                label: found.name || '',
                                value: nominee,
                                image_url: found.image_url || '',
                            }
                        }) || [],
                })) || [],
        })
    }

    const onSearchNominee = async (search) => {
        if (!search) {
            setOptionsNominee([])
            return
        }

        const response =
            type === NOMINEE_TYPE.PRODUCT ? await getProductList({ search }) : await getBrandList({ search })
        const newOptionProduct = response?.data?.map((item) => ({
            label: item.name,
            value: item.id,
            image_url: item.image_url,
        }))
        setOptionsNominee(newOptionProduct)
    }

    const onChangeForm = (e) => {
        const { name, value } = e
        setNominationDetail({ ...nominationDetail, [name]: value })
    }

    const handleUploadImage = async (files) => {
        let newFormImage = {}
        const fileNames = Object.entries(files).map(([key, item]) => item.name)

        const s3Urls = await getURLImage({ files: fileNames })
        const newEntries = Object.entries(files).map(([key, item], index) => {
            return [key, { ...item, ...s3Urls?.data?.[index] }]
        })

        const s3UrlsWithKey = Object.fromEntries(newEntries)
        await Promise.all(
            Object.entries(s3UrlsWithKey).map(async ([key, item], index) => {
                await uploadFileToS3(item, item.file)
                newFormImage = {
                    ...newFormImage,
                    [key]: `${s3UrlsWithKey?.[key]?.url}${s3UrlsWithKey?.[key]?.fields?.key}`,
                }
            }),
        )
        return newFormImage
    }

    const setItems = (items) => {
        setNominationDetail({ ...nominationDetail, nominations: items })
    }

    const onChangeItem = (e) => {
        const { name, value, index } = e
        copyItems[index][name] = value
        setItems(copyItems)
    }

    const onAddNewItem = () => {
        setItems([
            ...copyItems,
            {
                name: null,
                nominee_ids: [],
            },
        ])
    }

    const transformNomineeIds = (data) => {
        return data.map((item) => ({
            ...item,
            nominee_ids: item.nominee_ids.map((nominee) => nominee.value),
        }))
    }

    const validationForm = () => {
        const newErrorForm = { ...errorForm }
        if (!nominationDetail.title_image_url) {
            newErrorForm.title_image_url = 'Image title wajib diisi'
        } else {
            newErrorForm.title_image_url = ''
        }

        const errorArrayItem = []

        for (const item of nominationDetail.nominations) {
            const itemError = { name: '', nominee_ids: '' }
            if (!item.name) itemError.name = 'Title wajib diisi'
            if (item.nominee_ids?.length === 0) itemError.nominee_ids = 'Nominee wajib diisi'
            errorArrayItem.push(itemError)
        }

        newErrorForm.nominations = [...errorArrayItem]

        return newErrorForm
    }

    const isValid = (data) => {
        if (typeof data === 'string') {
            return data === ''
        }
        if (Array.isArray(data)) {
            return data.every(isValid)
        }
        if (typeof data === 'object' && data !== null) {
            return Object.values(data).every(isValid)
        }
        return false
    }

    const onSubmit = async () => {
        const findErrors = validationForm()
        setErrorForm(findErrors)
        if (!isValid(findErrors)) return

        if (moment(data?.start_date).isSameOrBefore(moment())) {
            return Alert({
                icon: 'warning',
                title: 'Warning',
                message: 'Hanya boleh edit End Date & Time selama award sedang berlangsung',
            })
        }

        let files = {}
        if (nominationDetail?.title_image_url?.file)
            files = { ...files, title_image_url: nominationDetail?.title_image_url }
        if (nominationDetail?.mini_banner_url?.file)
            files = { ...files, mini_banner_url: nominationDetail?.mini_banner_url }

        const isUploadFiles = Object.entries(files).length > 0

        const payload = {
            ...dataGeneral,
            start_date: moment(dataGeneral?.start_date).utc().format('YYYY-MM-DD HH:mm'),
            end_date: moment(dataGeneral?.end_date).utc().format('YYYY-MM-DD HH:mm'),
            categories: {
                ...dataGeneral.categories,
                [id]: {
                    ...nominationDetail,
                    nominations: transformNomineeIds(nominationDetail?.nominations),
                    ...(isUploadFiles && (await handleUploadImage(files))),
                },
            },
        }

        await updateAward(payload)
        onSuccessUpdate()
        Alert({
            icon: 'success',
            title: 'Success',
            message: `Award Updated`,
        })
    }

    const props = {
        data: nominationDetail,
        items: copyItems,
        errorForm,
        optionsNominee,
        setItems,
        onChangeForm,
        onChangeItem,
        onAddNewItem,
        onSearchNominee,
        onSubmit,
        isLoading: isLoading || isLoadingUpdate,
    }

    return <NominationVoting {...props} />
}
